
import React, { useEffect, useState } from 'react';
import { Row, Col, Alert as Alrt, Form, FormCheck, Badge, CloseButton } from 'react-bootstrap';
import Card from '../../../components/Card';

import { AlertItem } from '../../../features/alerts';
import { getAlerts } from '../../../services/api/alert';
import { ApiError, Alert } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web';

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { peekPendingSelectedId, clearPendingSelectedId } from '../../../hooks/pendingSelection';
import { resetNavContext } from '../../../hooks/navContext';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Locations = () => {
    const [alerts, setAlerts] = useState<Alert[]>([]);        
    const [alertCount, setAlertCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");    
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);
    const [formData, setFormData] = useLocalStorage<string>("alerts", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("alerts", "searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("alerts", "filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("alerts", "currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(() => peekPendingSelectedId("alerts"));
    const [rowClickCount, setRowClickCount] = useState({});

    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const setData = (data: Alert) => {
        localStorage.setItem('Alert', JSON.stringify(data));
    }

    const handleFilter = (e: string): void => {
        setAlerts([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSearch = (e: string): void => {
        setAlerts([]);
        searchData["search"] = e;
        setSearchData(searchData);
        formData === "" ? setFormData(" ") : setFormData("");       
        setCurrentPage(0);
    }

    const handleSearchColumn1 = (e: string): void => {
        setAlerts([]);
        searchData["alertTypeDescription"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn4 = (e: string): void => {
        setAlerts([]);
        searchData["alertSourceDescription"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setAlerts([]);
        searchData["startDateTime"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setAlerts([]);
        searchData["endDateTime"] = e;
        setSearchData(searchData);
        handleChange();
    }    

    const handleRowClick = (tx) => {
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [tx.id]: 1 });
        }
        
        setRowClickCount((prev) => {
            const newCount = (prev[tx.id] || 0) + 1;
            if (newCount === 1) {
            setSelectedId(tx.id);
            return { ...prev, [tx.id]: newCount };
            } else if (newCount === 2) {
            setData(tx);
            resetNavContext({ from: 'lists/alerts', id: null });
            navigate("/dashboard/details/alert");
            setSelectedId(tx.id);
            return { ...prev, [tx.id]: 0 };
            }
            return prev;
        });
    };

    const handleRefreshButton = (): void => {
        setAlerts([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);
    }

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);       

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);        

        getAlerts(keycloak.token || '', rangeString, { search: formData, Status: filterData, sites: siteFilter.join(), sitesQuery: siteFilterQuery, languageCode: i18n.language }, searchData)
            .then((result) => {
                setAlerts(result.body);
                var totalCount = result.header.split('/')[1];
                setAlertCount(totalCount);
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                } catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    };

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();
        if (alerts?.length === 0) {
            init();
        }
        clearPendingSelectedId("alerts");
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (alerts.length > 0) return;
        init();
    }, [formData, filterData]);

    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2 qvend-title">{t("alarmen")}</h4>                                    
                                </Col>
                            </Row>                            
                            <Row style={{ fontSize: '11px' }}>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                    <div className="bd-example">
                                        <div>
                                            <SearchInput className="form-control search" type="search" placeholder="Search" onChange={handleSearch} value={searchData.search ?? ""} />
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} xs={1}>
                                    <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>
                                </Col>                                    
                                <Col className="text-end">
                                    <button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>
                                </Col>
                            </Row>
                            <Row>
                            <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                            </Col>
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row>
                                    <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                                        <div>
                                            <fieldset className="form-control-sm" style={{ fontSize: '11px' }}>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("")
                                                    } type="radio" name="radios" className="form-check-input" id="radio1" defaultChecked={filterData === ""} />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio1">{t('alerts.filter.all')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("active")
                                                    } type="radio" name="radios" className="form-check-input" id="radio2" defaultChecked={filterData === "true"} />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio2">{t('alerts.filter.active')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("confirmed")
                                                    } type="radio" name="radios" className="form-check-input" id="radio3" defaultChecked={filterData === "false"} />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio3">{t('alerts.filter.confirmed')}</FormCheck.Label>
                                                </Form.Check>
                                                {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) ? <span className="text-end">
                                                    <Badge pill bg="dark" className="p-1 " onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setAlerts([]); setCurrentPage(0); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites") }}><CloseButton variant="white" /> Sites</Badge></span> : null}
                                            </fieldset>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                            <Row>
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col xl={4} lg={4} md={3} sm={4} xs={4} className="qvend-table table-column-first">{t('alert.alertType')}</Col>
                                                <Col xl={4} lg={4} md={3} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('alerts.column.sourceDescription')}</Col>
                                                <Col xl={2} lg={2} md={3} sm={4} xs={4} className="qvend-table">{t('alert.begin')}</Col>
                                                <Col xl={2} lg={2} md={3} sm={4} xs={4} className="qvend-table">{t('alert.end')}</Col>                                                
                                            </Row>                                            
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col xl={4} lg={4} md={3} sm={4} xs={4} className="qvend-table search-in-row-first"><SearchInput className="form-control alertTypeDescription" type="search" onChange={handleSearchColumn1} value={searchData.alertTypeDescription ?? ""} /></Col>
                                                <Col xl={4} lg={4} md={3} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control alertSourceDescription" type="search" onChange={handleSearchColumn4} value={searchData.alertSourceDescription ?? ""} /></Col>
                                                <Col xl={2} lg={2} md={3} sm={4} xs={4} className="qvend-table search-in-row-padding"><SearchInput className="form-control startDateTime" type="search" onChange={handleSearchColumn2} value={searchData.startDateTime ?? ""} /></Col>
                                                <Col xl={2} lg={2} md={3} sm={4} xs={4} className="qvend-table search-in-row-padding"><SearchInput className="form-control endDateTime" type="search" onChange={handleSearchColumn3} value={searchData.endDateTime ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alrt key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alrt>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                                {alerts.map((alert: Alert, index) => (
                                                    <Link to="#" onClick={() => handleRowClick(alert)} key={alert.id}>
                                                        <AlertItem
                                                            isSelected={selectedId === alert.id}
                                                            key={alert.id}
                                                            alert={alert}
                                                            rowColor={selectedId === alert.id ? "gray" :index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                        />
                                                    </Link>
                                                ))}
                                            {
                                                //Array.from({ ...dummyVmc, length: (tableBodyHeight/16.5) - vmcs.length } as VmcStatus[]).map((vmc: VmcStatus, index) => (
                                                //    <VmcStatusItem
                                                //        key={index+vmcs.length }
                                                //        vmcStatus={dummyVmc}
                                                //        rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                //        className=""
                                                //    />
                                                //))
                                            }
                                        </div>                                        
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={alertCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton } />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

export default Locations
